/**
 * The toolbar's image control (`061-editor.md` §2). A small menu with two ways in:
 *
 *  - **Upload file** — the file picker, the same `insertImages` path as a paste/drop (`062`
 *    §3). One upload choreography to get right, not two.
 *  - **From URL** — embed an external `http(s)` URL directly as the image `src` (`imageUrl.ts`).
 *    No download, no `assetId`; a deliberate, documented deviation from the `/uploads/` rule.
 */
import { useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { Image as ImageIcon, Link2, Upload } from 'lucide-react';
import { ALLOWED_MIME } from '@/api/endpoints/assets';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { insertImageFromUrl, parseImageUrl } from '@/features/editor/imageUrl';
import { insertImages, type UploadContext } from '@/features/editor/uploads';

const triggerClass = cn(
  'flex size-6 items-center justify-center rounded-sm text-[var(--muted-foreground)]',
  'hover:bg-[var(--accent)] hover:text-[var(--foreground)]',
  'data-[state=open]:bg-[var(--accent)] data-[state=open]:text-[var(--foreground)]',
  '[&_svg]:size-3.5',
);

export function ImageMenu({ editor, uploads }: { editor: Editor; uploads: UploadContext }) {
  const { t } = useTranslation();
  const file = useRef<HTMLInputElement>(null);
  const [urlOpen, setUrlOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [invalid, setInvalid] = useState(false);

  const submitUrl = (): void => {
    if (parseImageUrl(url) === null) {
      setInvalid(true);
      return;
    }
    void insertImageFromUrl(editor, url);
    setUrl('');
    setInvalid(false);
    setUrlOpen(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          type="button"
          aria-label={t('editor.toolbar.image')}
          title={t('editor.toolbar.image')}
          className={triggerClass}
        >
          <ImageIcon />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem
            onSelect={() => {
              file.current?.click();
            }}
          >
            <Upload />
            {t('editor.image.uploadFile')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => {
              setInvalid(false);
              setUrlOpen(true);
            }}
          >
            <Link2 />
            {t('editor.image.fromUrl')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* The SAME upload path as a paste — `insertImages` (`140-roadmap.md` M3). */}
      <input
        ref={file}
        type="file"
        accept={ALLOWED_MIME.join(',')}
        multiple
        hidden
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          event.target.value = ''; // so picking the same file twice fires again
          if (files.length > 0) void insertImages(editor, uploads, files, null);
        }}
      />

      <Dialog open={urlOpen} onOpenChange={setUrlOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('editor.image.fromUrl')}</DialogTitle>
          </DialogHeader>
          <input
            type="url"
            autoFocus
            value={url}
            placeholder={t('editor.image.urlPlaceholder')}
            onChange={(event) => {
              setUrl(event.target.value);
              setInvalid(false);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submitUrl();
              }
            }}
            className={cn(
              'h-[var(--control-h)] w-full rounded-md border bg-transparent px-2',
              'outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]',
              invalid ? 'border-[var(--destructive)]' : 'border-[var(--input)]',
            )}
          />
          {invalid ? (
            <p className="text-xs text-[var(--destructive)]">{t('editor.image.urlInvalid')}</p>
          ) : null}
          <DialogFooter>
            <button
              type="button"
              onClick={submitUrl}
              className={cn(
                'h-[var(--control-h)] rounded-md bg-[var(--primary)] px-3 font-medium',
                'text-[var(--primary-foreground)] hover:opacity-90',
              )}
            >
              {t('editor.image.add')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
